import { useState } from 'react';
import { ApiError } from '@nyce/admin-kit/api';
import { FormDialog, KeyValues, StatusChip } from '@nyce/admin-kit/console';
import { Button, Select, TextField } from '@nyce/admin-kit/ui';
import type { DbServerRef } from '../../api/db';
import {
  Card,
  EmptyState,
  ErrorBox,
  Loading,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
} from '../../components/common';
import { formatCount, formatDateTime } from '../../lib/format';
import { serverName, useServerDatabases } from '../../pages/databases/shared';
import { indexAdvisor, type DefaultSetIndex, type IndexAdvisorSettings } from '../api/indexAdvisor';
import { advisorErrorText, useParam, type Notify } from '../shared';

/**
 * The default set every tenant gets: the platform template database's indexes plus `index-additions.sql`
 * (`@class general|conditional`, `@why`, `@cost`). Candidates are checked against it.
 */
export function DefaultSetTab({
  advise,
  settings,
  settingsError,
  onSaved,
  servers,
  notice,
}: {
  advise: boolean;
  settings: IndexAdvisorSettings | null;
  settingsError: string | null;
  onSaved: (s: IndexAdvisorSettings) => void;
  servers: DbServerRef[];
  notice: Notify;
}) {
  const [version, setVersion] = useState(0);
  const set = useAsync(() => indexAdvisor.defaultSet(), [version]);
  const [editing, setEditing] = useState(false);
  const [source, setSource] = useParam('source', '');
  const [q, setQ] = useParam('q', '');
  const needle = q.trim().toLowerCase();
  const all = set.data?.indexes ?? [];
  const filtered = all.filter(
    (x) =>
      (!source || x.source === source) &&
      (!needle || `${x.name} ${x.table ?? ''} ${x.sql}`.toLowerCase().includes(needle))
  );
  const sort = useSort<DefaultSetIndex>(
    filtered,
    {
      table: { label: 'Table', value: (x) => x.table },
      name: { label: 'Name', value: (x) => x.name },
      source: { label: 'Source', value: (x) => x.source },
      class: { label: 'Class', value: (x) => x.class },
    },
    useParam('sort', '')
  );
  const additions = all.filter((x) => x.source === 'additions').length;
  const template =
    settings?.templateServerId && settings.templateDatabase
      ? `${serverName(servers, settings.templateServerId)}/${settings.templateDatabase}`
      : null;
  return (
    <>
      <ErrorBox error={settingsError} />
      <Card
        title="Default set"
        actions={
          advise && settings ? (
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : undefined
        }
      >
        <KeyValues
          rows={[
            ['Template database', template ?? <span className="iax-muted">none: additions only</span>],
            ['Template indexes', set.data ? formatCount(all.length - additions) : null],
            ['Additions', set.data ? formatCount(additions) : null],
            [
              'Updated',
              settings?.updatedAt
                ? `${formatDateTime(settings.updatedAt)}${settings.updatedBy ? ` by ${settings.updatedBy}` : ''}`
                : null,
            ],
          ]}
        />
        {set.data?.error && <ErrorBox error={`The template could not be read: ${set.data.error}`} />}
      </Card>
      <div className="iax-toolbar">
        <Select
          aria-label="Source"
          value={source}
          onChange={setSource}
          options={[
            { value: '', label: 'Template and additions' },
            { value: 'template', label: 'Template' },
            { value: 'additions', label: 'Additions' },
          ]}
        />
        <TextField
          aria-label="Search"
          placeholder="Name, table or column"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <SortSelect sort={sort} />
      </div>
      <ErrorBox error={set.error} />
      <Loading loading={set.loading && !set.data} />
      {set.data && !all.length && (
        <EmptyState
          card
          title="The default set is empty."
          hint={advise ? 'Pick a template database and add index additions.' : undefined}
        />
      )}
      {!!sort.rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" aria-label="Default set" data-testid="iax-default-set">
              <thead>
                <tr>
                  <SortTh sort={sort} by="name" />
                  <SortTh sort={sort} by="table" />
                  <th>Definition</th>
                  <SortTh sort={sort} by="source" />
                  <SortTh sort={sort} by="class" />
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {sort.rows.map((x) => (
                  <tr key={`${x.source}/${x.name}`} data-index={x.name}>
                    <td className="nyk-card-title nyk-wrap">{x.name}</td>
                    <td data-label="Table" className="nyk-wrap">
                      {x.table ?? '—'}
                    </td>
                    <td data-label="Definition">
                      <span className="iax-ddl">{x.sql}</span>
                    </td>
                    <td data-label="Source">
                      <StatusChip
                        value={x.source}
                        tone={x.source === 'template' ? 'grey' : 'blue'}
                        label={x.source}
                      />
                    </td>
                    <td data-label="Class" className="nyk-wrap">
                      {x.class ?? '—'}
                      {x.when && <div className="nyk-ui-hint">when {x.when}</div>}
                    </td>
                    <td data-label="Why" className="nyk-wrap">
                      {x.why ?? '—'}
                      {x.cost && <div className="nyk-ui-hint">cost: {x.cost}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && settings && (
        <EditDialog
          settings={settings}
          servers={servers}
          onClose={() => setEditing(false)}
          onSaved={(s, n) => {
            setEditing(false);
            onSaved(s);
            setVersion((v) => v + 1);
            notice.success(`Default set saved (${n} additions)`);
          }}
        />
      )}
    </>
  );
}

function EditDialog({
  settings,
  servers,
  onClose,
  onSaved,
}: {
  settings: IndexAdvisorSettings;
  servers: DbServerRef[];
  onClose: () => void;
  onSaved: (s: IndexAdvisorSettings, additions: number) => void;
}) {
  const [serverId, setServerId] = useState(settings.templateServerId ?? '');
  const [database, setDatabase] = useState(settings.templateDatabase ?? '');
  const [sql, setSql] = useState(settings.additionsSql);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sqlError, setSqlError] = useState<string | null>(null);
  const dbs = useServerDatabases(serverId);
  const valid = !serverId === !database;
  const submit = async () => {
    setBusy(true);
    setError(null);
    setSqlError(null);
    try {
      const saved = await indexAdvisor.saveSettings({
        templateServerId: serverId || null,
        templateDatabase: database || null,
        additionsSql: sql,
      });
      onSaved({ ...settings, ...saved }, saved.additions);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'invalid_additions') setSqlError(e.message);
      else setError(advisorErrorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormDialog
      title="Edit the default set"
      size="lg"
      onClose={onClose}
      onSubmit={() => void submit()}
      canSubmit={valid}
      busy={busy}
      error={error}
      draft={{ serverId, database, sql }}
      formClassName="iax-form"
    >
      <div className="iax-form-row">
        <Select
          label="Template server"
          value={serverId}
          onChange={(v) => {
            setServerId(v);
            setDatabase('');
          }}
          options={[
            { value: '', label: 'No template' },
            ...servers.map((s) => ({ value: s.id, label: s.name || s.id })),
          ]}
        />
        {serverId ? (
          <Select
            label="Template database"
            value={database}
            placeholder="Pick a database"
            onChange={setDatabase}
            options={(!database || dbs.names.includes(database) ? dbs.names : [database, ...dbs.names]).map(
              (d) => ({ value: d, label: d })
            )}
          />
        ) : (
          <span />
        )}
      </div>
      {dbs.error && <ErrorBox error={dbs.error} />}
      <p className="nyk-ui-hint">
        The template is a native tenant database: its indexes are read live. Without one, only the additions
        count.
      </p>
      <TextField
        label="index-additions.sql"
        multiline
        rows={16}
        spellCheck={false}
        fieldClassName="iax-additions"
        value={sql}
        onChange={(e) => {
          setSql(e.target.value);
          setSqlError(null);
        }}
        error={sqlError ?? undefined}
        hint="One CREATE INDEX per block, with -- @class general|conditional, @when, @why and @cost comments (the toolkit's format)."
      />
    </FormDialog>
  );
}
